import React, { Fragment, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { ButtonModalClose } from "./ButtonModalClose";
import { useAuth } from "../context/AuthContext";
import AxisSpinner from "./AxisSpinner";
import "chart.js/auto";
import { Pie } from "react-chartjs-2";
import { AxisLineSeparator } from "./AxisLineSeparator";
import { TimesheetWeekDownload } from "./TimesheetWeekDownload";
import { TimesheetWeekTrustBreakDown } from "./TimesheetWeekTrustBreakDown";
import { BsArrowClockwise } from "react-icons/bs";

export const TimesheetWeekDisplay = ({ timesheetWeek, close = null }) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState([]);
  const [tallyData, setTallyData] = useState(null);
  const [chartTally, setChartTally] = useState(null);
  const [chartTallyOptions, setChartTallyOptions] = useState(null);
  const [user, setUser] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (timesheetWeek === null || timesheetWeek === undefined) return;

    getOverview();
    fetchCurrentUser();
  }, [timesheetWeek]);

  useEffect(() => {
    if (tallyData != null) {
      setChartTally({
        labels: tallyData.labels,
        datasets: [
          {
            label: "Timesheets",
            data: tallyData.data,
            backgroundColor: ["#63e6be", "#ff8c00", "#9e70ff", "#e66566"],
            hoverOffset: 50,
          },
        ],
      });
      setChartTallyOptions({
        plugins: {
          tooltip: {
            titleFont: {
              size: 18,
            },
            bodyFont: {
              size: 14,
              weight: "bold",
            },
            backgroundColor: "#00005F",
            borderColor: "#EEEFEF",
            borderWidth: 2,
          },
          legend: {
            labels: {
              font: {
                size: 14,
                weight: "bold",
              },
              color: "#00005F",
            },
          },
        },
      });
    }
  }, [tallyData]);

  const fetchCurrentUser = async () => {
    try {
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {},
      };

      const response = await authAxios().get(
        "/api/backoffice/users/current_user",
        config,
      );

      setUser(response.data);
    } catch (error) {
      console.log("Error fetching current user");
      console.log(error);
    }
  };

  const refreshDownloads = async () => {
    try {
      setRefreshing(true);

      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {
          timesheet_week_id: timesheetWeek?.id,
        },
      };

      await authAxios().post(
        "/api/backoffice/reporting/timesheet_week_regen",
        null,
        config,
      );

      // Refresh the overview data after regeneration
      await getOverview();
      setRefreshing(false);
      
      // Show success message
      alert("Download refresh initiated successfully. This can take a few minutes, please come back later to see the updated downloads.");
    } catch (error) {
      setRefreshing(false);
      console.log("Error refreshing downloads");
      console.log(error);
      alert("Error refreshing downloads. Please try again.");
    }
  };

  const getOverview = async () => {
    try {
      setLoading(true);

      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {
          timesheet_week_id: timesheetWeek?.id,
        },
      };

      const response = await authAxios().get(
        "/api/backoffice/reporting/timesheet_week_overview",
        config,
      );

      setOverview(response.data);

      var labels = ["Approved", "Missing", "Pending", "Rejected"];
      var data = [
        response?.data?.approvedTimesheets?.length,
        response?.data?.missingTimesheets?.length,
        response?.data?.pendingTimesheets?.length,
        response?.data?.rejectedTimesheets?.length,
      ];
      setTallyData({
        labels: labels,
        data: data,
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
  };

  const getDownloadOptions = () => {
    return <div>Download Options</div>;
  };

  const getTotalTallyDisplay = (totalTally) => {
    if (totalTally === null || totalTally === undefined) return;
    return (
      <Fragment>
        {totalTally.labels.map((item, i) => (
          <li key={i}>
            {item} : {totalTally.data[i]}
          </li>
        ))}
      </Fragment>
    );
  };

  const displayChartTotalTally = (chartTotalTally) => {
    if (chartTotalTally === null || chartTotalTally === undefined) return;
    return (
      <Card className="shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">
            <i className="fas fa-chart-pie me-2"></i>
            Timesheet Status Overview
          </h5>
        </Card.Header>
        <Card.Body className="py-4">
          <Pie
            className={"w-100"}
            data={chartTotalTally}
            options={chartTallyOptions}
          />
        </Card.Body>
      </Card>
    );
  };

  const isAdminUser = () => {
    return user?.role === "ADMIN" || user?.role === "SUPERADMIN";
  };

  const getChartOverview = () => {
    return (
      <Fragment>
        <Row xs={1} md={2} className={"mt-4"}>
          {/* Downloads on the left */}
          <Col className={"justify-content-center align-items-center"}>
            <TimesheetWeekDownload timesheetWeek={timesheetWeek} />
            {isAdminUser() && (
              <div className="mt-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={refreshDownloads}
                  disabled={refreshing}
                  className="d-flex align-items-center"
                >
                  <BsArrowClockwise className={`me-2 ${refreshing ? 'spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh Downloads'}
                </Button>
              </div>
            )}
          </Col>
          
          {/* Trust breakdown above the pie chart on the right */}
          <Col>
            <div className="mb-4">
              <TimesheetWeekTrustBreakDown timesheetWeek={timesheetWeek} />
            </div>
            <div className="mt-4">
              {displayChartTotalTally(chartTally)}
            </div>
          </Col>
        </Row>
      </Fragment>
    );
  };

  const SpinnerOrData = (loading) => {
    if (loading) return <AxisSpinner loading={loading} />;
    return <Fragment>{getChartOverview()}</Fragment>;
  };

  return (
    <Fragment>
      <Container fluid>{SpinnerOrData(loading)}</Container>

      <Modal.Footer className={"mb-0 pb-0"}>
        <Container fluid>
          <Row className="mb-3">
            <Col className="d-flex justify-content-end">
              <ButtonModalClose
                className={"w-50"}
                close={close}
                buttonText={"Close"}
              />
            </Col>
          </Row>
        </Container>
      </Modal.Footer>
    </Fragment>
  );
};
